import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import {
  issueStateFormatter,
  getLocaleDateTimeString,
  formatPhoneNumber,
  history,
} from '../../utils';
import {
  sendReplyEmail,
  updateIssue,
  addNoteToReport,
  addTagsToErrorReport,
} from '../../api/problems';
import CommunicationLog from '../../components/communicationlog';
import { ticketColumnsV2 } from '../../tableColumns/hardwareColumns';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import Typography from '@mui/material/Typography';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import CustomToast from '../../components/Redesing/custom-toast';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import CardView from '../../components/Redesing/card-view';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import { Stack } from '@mui/system';
import Grid from '@mui/material/Unstable_Grid2';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { MovaticFieldGroupSelectFilter } from '../../components/Redesing/movatic-field-group';
import { getCredentials } from '../../api/http';
import useSmallScreen from '../../hooks/useSmallScreen';
import Chip from '@mui/material/Chip';
import useOrganization from '../../hooks/use-organization';
import ChangeOrganizationToSystem from '../../hooks/system/hook/ChangeOrganizationToSystem';
import { Divider } from '@mui/material';

// GraphQL hooks
import {
  useSupportTicketDetailSmartQuery,
  useTagsRelayQuery,
  mapRelaySupportTicketToIProblemDetails,
  mapRelayTagsToITags,
} from '../../hooks/supportTickets/query/V2';
import { IAdmin } from '../../ts/interface/admin.interface';
import { IProblemDetails } from '../../ts/interface/problem.interface';

interface ProblemDetailsProps extends RouteComponentProps<{ problemId: string }> {
  admin: IAdmin;
  system: unknown;
  systemLoaded: boolean;
}

function ProblemDetails(props: ProblemDetailsProps) {
  const organizationInfo = useOrganization();

  const { problemId } = props.match.params;
  const { systemGid } = getCredentials();
  const [note, setNote] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [addTagModal, setAddTagModal] = useState(false);
  const [value, setValue] = useState<{ label: string; value: string }[]>([]);
  const [deleteTagModal, setDeleteTagModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState('');
  const isSmallScreen = useSmallScreen();

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortData, setSortData] = useState('due_on');
  const [search, setSearch] = React.useState('');

  // Use GraphQL hook for problem detail
  const {
    data: problemData,
    isLoading: problemIsLoading,
    refetch: problemRefetch,
  } = useSupportTicketDetailSmartQuery(problemId, {
    enabled: Boolean(organizationInfo?.id) ? true : props.systemLoaded,
  });

  // Map GraphQL data to IProblemDetails format
  const problem = useMemo((): IProblemDetails => {
    if (!problemData) return {} as IProblemDetails;
    return mapRelaySupportTicketToIProblemDetails(problemData);
  }, [problemData]);

  // Use GraphQL hook for tags
  const { data: tagsData, isLoading: tagsLoading } = useTagsRelayQuery(
    {
      systemId: systemGid || problem?.system_info?.id,
      organizationId: organizationInfo?.id || undefined,
      archived: false,
    },
    {
      enabled: Boolean(organizationInfo?.id) ? true : props.systemLoaded,
    }
  );

  // Map GraphQL tags to ITag format
  const mappedTags = useMemo(
    () => (tagsData ? mapRelayTagsToITags(tagsData) : []),
    [tagsData]
  );

  const loadedTags = useMemo(
    () =>
      mappedTags
        .filter(
          (item) =>
            !organizationInfo?.id ||
            (!problem?.tags?.some((tag) => tag.name === item.name) &&
              problem?.system_info?.id === item?.system_info?.id)
        )
        .map((item) => ({
          label: item.name,
          value: item.id,
        })),
    [mappedTags, problem, organizationInfo]
  );

  const tickets = useMemo(() => problem?.maintenanceTickets || [], [problem]);

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value);
  };

  const sendEmail = async () => {
    if (note !== '') {
      const email = {
        message: note.toString(),
      };

      const result = await CustomAsyncToast({
        promise: () => sendReplyEmail(email, problem.id, problem?.system_info?.old_id),
        successMessage: () => 'Email sent successfully',
        loadingMessage: 'Sending email...',
        errorMessage:
          'Oops! Looks like there was an error while sending the email. Please try again later.',
      });

      setShowEmailModal(false);
      if (result) {
        setNote('');
        problemRefetch();
      }
    } else {
      CustomToast({
        type: 'error',
        message: 'Message cannot be blank',
      });
    }
  };

  const addInternalNote = () => {
    if (note !== '') {
      const newNote = JSON.stringify({
        admin_id: props.admin.email,
        created_on: new Date().toISOString(),
        message: note.toString(),
        type: 'internal',
      });

      CustomAsyncToast({
        promise: () =>
          addNoteToReport(
            { note: newNote, id: problem.id },
            problem?.system_info?.old_id
          ),
        successMessage: () => 'Note added successfully',
        loadingMessage: 'Adding note...',
        errorMessage:
          'Oops! Looks like there was an error while adding the note. Please try again later.',
      }).then((results) => {
        if (results) {
          setNote('');
          problemRefetch();
        }
      });
    } else {
      CustomToast({
        type: 'error',
        message: 'Message cannot be blank',
      });
    }
  };

  const handleCloseTicket = () => {
    const problemAux = { ...problem, state: 1 };
    CustomAsyncToast({
      promise: () => updateIssue(problemAux, problem?.system_info?.old_id),
      successMessage: () => 'Support ticket closed successfully',
      loadingMessage: 'Closing problem report...',
      errorMessage:
        'Oops! Looks like there was an error while closing the support ticket. Please try again later.',
    }).then((results) => {
      if (results) {
        problemRefetch();
        history.push('/problems');
      }
    });
    setShowCloseModal(false);
  };

  const handleReopenTicket = () => {
    const problemAux = { ...problem, state: 0 };
    CustomAsyncToast({
      promise: () => updateIssue(problemAux, problem?.system_info?.old_id),
      successMessage: () => 'Support ticket reopened successfully',
      loadingMessage: 'Reopening problem report...',
      errorMessage:
        'Oops! Looks like there was an error while reopening the problem report. Please try again later.',
    }).then((results) => {
      if (results) {
        problemRefetch();
      }
    });
  };

  const handleAddTags = () => {
    CustomAsyncToast({
      promise: () =>
        addTagsToErrorReport(
          problem.gid,
          {
            tags: [
              ...(problem?.tags?.map((t) => t.name) || []),
              ...value.map((item) => item.label),
            ],
          },
          problem?.system_info?.old_id
        ),
      successMessage: () => 'Tags added successfully',
      loadingMessage: 'Adding tags...',
      errorMessage:
        'Oops! Looks like there was an error while adding the tags. Please try again later.',
    }).then((results) => {
      if (results) {
        setAddTagModal(false);
        problemRefetch().then(() => {
          setValue([]);
        });
      }
    });
  };

  const handleRemoveTag = () => {
    CustomAsyncToast({
      promise: () =>
        addTagsToErrorReport(
          problem.gid,
          {
            tags: problem.tags
              ? problem.tags.filter((item) => item.name !== tagToDelete).map((t) => t.name)
              : [],
          },
          problem?.system_info?.old_id
        ),
      successMessage: () => 'Tag removed successfully',
      loadingMessage: 'Removing tag...',
      errorMessage:
        'Oops! Looks like there was an error while removing the tag. Please try again later.',
    }).then((results) => {
      if (results) {
        setDeleteTagModal(false);
        problemRefetch().then(() => {
          setTagToDelete('');
        });
      }
    });
  };

  const content = () => {
    return (
      <PropertyList>
        <PropertyListItemLink
          others={{ divider: true }}
          label="User"
          text={problem.userName}
          onClick={() =>
            history.push(`/users/${problem.userId}/general`)
          }
          dataId="user"
        />
        <PropertyListItem
          divider
          label="Email Address"
          value={problem.email}
          dataId="email"
        />
        <PropertyListItem
          divider
          label="Phone Number"
          value={formatPhoneNumber(problem.userId)}
          dataId="phone"
        />
        <PropertyListItem
          divider
          label="Created"
          value={getLocaleDateTimeString(problem.created)}
          dataId="created"
        />
        <PropertyListItem divider label="Current Status" dataId="status">
          <SeverityPill color={problem.state === 1 ? 'success' : 'warning'}>
            {issueStateFormatter(problem.state)}
          </SeverityPill>
        </PropertyListItem>
        {issueStateFormatter(problem.state) === 'Closed' ? (
          <PropertyListItem
            divider
            label="Closed"
            value={getLocaleDateTimeString(problem.closedDate || '')}
          />
        ) : null}
        <PropertyListItem
          divider
          label="Owner"
          value={problem.owner || 'Unassigned'}
          dataId="owner"
        />
        {problem.hardware_id && (
          <PropertyListItemLink
            others={{ divider: true }}
            label="Hardware"
            text={`${problem.hardware_id}`}
            onClick={() =>
              history.push(
                `/hardware/${
                  organizationInfo?.id ? problem.hardware_gid : problem.hardware_id
                }${
                  organizationInfo?.id
                    ? `/general?organizationId=${organizationInfo.id}`
                    : ''
                }`
              )
            }
            dataId="hardware"
          />
        )}
        {problem.rental_gid ? (
          <PropertyListItemLink
            others={{ divider: true }}
            label="Rental"
            text={problem.rental_gid}
            onClick={() => history.push(`/rentals/${problem.rental_gid}`)}
            dataId="rental"
          />
        ) : null}
        {Boolean(organizationInfo?.id) && (
          <>
            <ChangeOrganizationToSystem system={problem?.system_info || {}} />
            <Divider />
          </>
        )}
        <PropertyListItem divider label="Tags" dataId="tags">
          <Grid container spacing={1} data-testid="tags-container">
            {problem?.tags && problem?.tags.length > 0 && (
              <>
                {problem?.tags.map((tag) => (
                  <Grid key={tag.name}>
                    <Chip
                      sx={{
                        height: 'auto',
                        py: 0.5,
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'normal',
                        },
                      }}
                      onDelete={() => {
                        setTagToDelete(tag.name);
                        setDeleteTagModal(true);
                      }}
                      label={tag.name}
                    />
                  </Grid>
                ))}
              </>
            )}
            <Grid>
              <IconButtonMenu
                buttons={[
                  {
                    label: '',
                    startIcon: <AddIcon />,
                    dataId: 'add-tag-button',
                    onClick: () => setAddTagModal(true),
                  },
                ]}
              />
            </Grid>
          </Grid>
        </PropertyListItem>
      </PropertyList>
    );
  };

  const button = () => {
    return (
      <IconButtonMenu
        buttons={[
          {
            label: problem.state === 0 ? 'Close Ticket' : 'Reopen Ticket',
            startIcon: problem.state === 0 ? <CloseIcon /> : <LockOpenIcon />,
            onClick:
              problem.state === 0 ? () => setShowCloseModal(true) : handleReopenTicket,
          },
        ]}
      />
    );
  };

  const maintenanceContent = () => {
    return (
      <>
        <CardView
          isLoading={problemIsLoading}
          headerActions={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Add',
                  onClick: () =>
                    history.push(`/maintenance/add/report/${problem.gid}`),
                  startIcon: <AddIcon />,
                },
              ]}
            />
          }
          content={
            <PaginationTable
              padding={{
                pl: -1,
                pt: 1,
                pr: 1,
              }}
              sortBy={sortData}
              showFilter={false}
              onSortChange={(sortBy) => setSortData(sortBy.key)}
              items={tickets}
              noDataText={'No Maintenance Tickets'}
              onRowCLick={(id) => history.push(`/maintenance/${id}`)}
              customKey="gid"
              columns={ticketColumnsV2}
              rowsPerPage={pageSize}
              page={pageNo}
              onPageChange={(page, pageN) => {
                setPageNo(pageN);
              }}
              onRowsPerPageChange={(rowsPerPage) =>
                setPageSize(Number(rowsPerPage.target.value))
              }
              searchText={search}
              searchPlaceholder={`Search maintenance tickets`}
              handleSearch={(searchEvent) => setSearch(searchEvent.target.value)}
            />
          }
          title={'Maintenance Tickets'}
        />
      </>
    );
  };

  return (
    <PageContainer>
      <PageContent
        backLink={'/problems'}
        contentTitle={'Support Tickets'}
        itemName={'Support Ticket'}
        itemChipValue={problemId}
        detailIcon={<SupportAgentIcon />}
        chipLabel={'Ticket ID'}
        dataId="ticket-details"
        tabsChildren={
          <Grid container spacing={2}>
            <Grid xs={12} lg={4}>
              <Stack spacing={2}>
                <CardView
                  content={content()}
                  title="Support Ticket Details"
                  isLoading={problemIsLoading}
                  dataId="ticket-details"
                />
              </Stack>
            </Grid>
            <Grid xs={12} lg={8}>
              <Stack spacing={2}>
                <CommunicationLog
                  ticketDescription={
                    <>
                      <Box className="messageRow">
                        <Box className="flexDisplay flexWrap">
                          <Typography variant="subtitle1" className="topRow">
                            Description
                          </Typography>
                        </Box>
                        <>
                          {problem.content
                            ? problem.content.split('\n').map((p, i) => (
                                <Typography
                                  align={'justify'}
                                  variant="body2"
                                  className="messageContent"
                                  key={i}
                                >
                                  {p.toString()}
                                </Typography>
                              ))
                            : ''}{' '}
                        </>
                      </Box>
                    </>
                  }
                  buttons={2}
                  log={problem.communication_log}
                  note={note}
                  placeholder="New message"
                  noNoteText="No communication for this support ticket"
                  handleNoteChange={handleNoteChange}
                  saveNote={() => setShowEmailModal(true)}
                  saveButtonText="Send to User"
                  secondarySaveNote={addInternalNote}
                  loaded={!problemIsLoading}
                />
                {maintenanceContent()}
              </Stack>
            </Grid>
          </Grid>
        }
        itemTopButtons={button()}
      />

      <MovaticCustomModal
        open={showCloseModal}
        close={() => setShowCloseModal(false)}
        content="Are you sure you want to close this support ticket?"
        title="Close Support Ticket"
        action={
          <IconButtonMenu
            buttons={[
              { label: 'No', onClick: () => setShowCloseModal(false) },
              {
                label: 'Yes',
                onClick: handleCloseTicket,
              },
            ]}
          />
        }
      />

      <MovaticCustomModal
        open={showEmailModal}
        close={() => setShowEmailModal(false)}
        content="Send this email to the customer?"
        title="Contact User"
        action={
          <IconButtonMenu
            buttons={[
              { label: 'Cancel', onClick: () => setShowEmailModal(false) },
              { label: 'Confirm', onClick: sendEmail },
            ]}
          />
        }
      />

      <MovaticCustomModal
        open={addTagModal}
        close={() => setAddTagModal(false)}
        content={
          <MovaticFieldGroupSelectFilter
            label={'Tags'}
            isLoading={tagsLoading}
            value={value}
            dataId="add-tag-select"
            onChange={(event, newValue) => {
              setValue([
                ...newValue.filter((option) =>
                  loadedTags.some((item) => item.label === option.label)
                ),
              ]);
            }}
            options={loadedTags}
          />
        }
        title="Add Tag"
        action={
          <IconButtonMenu
            buttons={[
              { label: 'Cancel', onClick: () => setAddTagModal(false) },
              {
                label: 'Save',
                disabled: loadedTags?.length === 0,
                dataId: 'add-tag-save-button',
                onClick: handleAddTags,
              },
            ]}
          />
        }
      />
      <MovaticCustomModal
        customStyles={{
          content: {
            minWidth: 'none',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isSmallScreen ? '90vw' : '30vw',
            margin: isSmallScreen ? 1 : 'auto',
            width: '100%',
          },
        }}
        open={deleteTagModal}
        onClose={() => setDeleteTagModal(false)}
        content={`Are you sure you want to remove the tag ${tagToDelete}?`}
        title={'Remove Tag'}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setDeleteTagModal(false),
              },
              {
                label: 'Submit',
                onClick: handleRemoveTag,
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
}

export default connect(
  (state: any) => ({
    admin: state.admin.admin.admin,
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(ProblemDetails);
